import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { User, Building, Users, DollarSign, Send, CheckCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TeacherProfile = ({ user }) => {
  const [teacherData, setTeacherData] = useState(null);
  const [centerData, setCenterData] = useState(null);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeStructure, setFeeStructure] = useState(null);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [proposedFees, setProposedFees] = useState({
    offline_monthly: '',
    offline_quarterly: '',
    offline_yearly: '',
    online_monthly: '',
    online_quarterly: '',
    online_yearly: '',
    registration_fee: '',
    kit_fee: ''
  });

  useEffect(() => {
    fetchTeacherData();
    fetchCenterData();
    fetchStudents();
    fetchBatches();
    fetchFeeStructure();
  }, []);

  const fetchTeacherData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeacherData(response.data);
    } catch (error) {
      console.error('Error fetching teacher data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCenterData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (user.center_id) {
        const response = await axios.get(`${API}/api/centers/${user.center_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCenterData(response.data);
      }
    } catch (error) {
      console.error('Error fetching center data:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/users?role=student&center_id=${user.center_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchFeeStructure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.length > 0) {
        setFeeStructure(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching fee structure:', error);
    }
  };

  const handleRequestFeeStructure = async () => {
    if (!requestMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    // Check if at least one fee is proposed
    const hasProposedFees = Object.values(proposedFees).some(val => val !== '');
    if (!hasProposedFees) {
      toast.error('Please propose at least one fee amount');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/fee-structure-requests`,
        {
          teacher_id: user.id,
          teacher_name: user.name,
          center_id: user.center_id,
          center_name: centerData?.name || 'N/A',
          message: requestMessage,
          proposed_fees: proposedFees,
          status: 'pending'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fee structure proposal sent successfully!');
      setRequestDialog(false);
      setRequestMessage('');
      setProposedFees({
        offline_monthly: '',
        offline_quarterly: '',
        offline_yearly: '',
        online_monthly: '',
        online_quarterly: '',
        online_yearly: '',
        registration_fee: '',
        kit_fee: ''
      });
    } catch (error) {
      console.error('Error sending request:', error);
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card className="p-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-2xl font-bold">
            {teacherData?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {teacherData?.name}
            </h2>
            <p className="text-gray-600">{teacherData?.email}</p>
            <div className="flex gap-4 mt-2">
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                Teacher
              </span>
              {teacherData?.is_center_head && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                  Center Head
                </span>
              )}
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3 bg-blue-50">
              <p className="text-xs text-gray-600">Students</p>
              <p className="text-2xl font-bold text-blue-600">{students.length}</p>
            </Card>
            <Card className="p-3 bg-green-50">
              <p className="text-xs text-gray-600">Batches</p>
              <p className="text-2xl font-bold text-green-600">{batches.length}</p>
            </Card>
          </div>
        </div>
      </Card>

      {/* Profile Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="personal">
            <User className="w-4 h-4 mr-2" />
            Personal Info
          </TabsTrigger>
          <TabsTrigger value="center">
            <Building className="w-4 h-4 mr-2" />
            Center Info
          </TabsTrigger>
          <TabsTrigger value="students">
            <Users className="w-4 h-4 mr-2" />
            My Students
          </TabsTrigger>
          <TabsTrigger value="fee-structure">
            <DollarSign className="w-4 h-4 mr-2" />
            Fee Structure
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab */}
        <TabsContent value="personal">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-semibold">{teacherData?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{teacherData?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <p className="font-semibold">Teacher</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Center Head</p>
                <span className={`px-3 py-1 text-sm rounded-full ${
                  teacherData?.is_center_head 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {teacherData?.is_center_head ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Joined On</p>
                <p className="font-semibold">
                  {teacherData?.created_at 
                    ? new Date(teacherData.created_at).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Center Info Tab */}
        <TabsContent value="center">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Center Information</h3>
            {centerData ? (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Center Name</p>
                  <p className="font-semibold">{centerData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-3 py-1 text-sm rounded-full ${
                    centerData.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {centerData.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Learning Mode</p>
                  <p className="font-semibold uppercase">{centerData.learning_mode || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone Number</p>
                  <p className="font-semibold">{centerData.phone_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <p className="font-semibold">{centerData.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">State</p>
                  <p className="font-semibold">{centerData.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Registration Date</p>
                  <p className="font-semibold">
                    {centerData.registration_date 
                      ? new Date(centerData.registration_date).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Renewal Date</p>
                  <p className="font-semibold">
                    {centerData.renewal_date 
                      ? new Date(centerData.renewal_date).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-semibold">{centerData.address || 'N/A'}</p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No center assigned</p>
            )}
          </Card>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">My Students</h3>
              <div className="text-sm text-gray-600">
                Total: <span className="font-bold text-blue-600">{students.length}</span> students
              </div>
            </div>

            {students.length > 0 ? (
              <div className="border rounded-lg overflow-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">GK Code</th>
                      <th className="px-4 py-2 text-left">Module</th>
                      <th className="px-4 py-2 text-left">Group</th>
                      <th className="px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-2">{student.name}</td>
                        <td className="px-4 py-2">{student.gk_code || 'N/A'}</td>
                        <td className="px-4 py-2 uppercase">{student.module || 'N/A'}</td>
                        <td className="px-4 py-2 uppercase">{student.group || 'N/A'}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            student.status === 'active' ? 'bg-green-100 text-green-800' :
                            student.status === 'on_break' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {student.status?.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No students assigned</p>
            )}
          </Card>
        </TabsContent>

        {/* Fee Structure Tab */}
        <TabsContent value="fee-structure">
          <div className="space-y-4">
            {/* Header with Request Button */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Fee Structure
              </h3>
              <Button
                onClick={() => setRequestDialog(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Request Fee Structure
              </Button>
            </div>

            {feeStructure ? (
              <div className="space-y-6">
                {/* Offline Fees */}
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-blue-900 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Offline Fees
                    </h4>
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                      In-Person
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="p-4 bg-white border-blue-200">
                      <p className="text-xs text-gray-600 mb-1">Monthly</p>
                      <p className="text-3xl font-bold text-blue-600">₹{feeStructure.offline_monthly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per month</p>
                    </Card>
                    <Card className="p-4 bg-white border-blue-200">
                      <p className="text-xs text-gray-600 mb-1">Quarterly</p>
                      <p className="text-3xl font-bold text-blue-600">₹{feeStructure.offline_quarterly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per 3 months</p>
                    </Card>
                    <Card className="p-4 bg-white border-blue-200">
                      <p className="text-xs text-gray-600 mb-1">Yearly</p>
                      <p className="text-3xl font-bold text-blue-600">₹{feeStructure.offline_yearly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per year</p>
                    </Card>
                  </div>
                </Card>

                {/* Online Fees */}
                <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-green-900 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Online Fees
                    </h4>
                    <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                      Virtual
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="p-4 bg-white border-green-200">
                      <p className="text-xs text-gray-600 mb-1">Monthly</p>
                      <p className="text-3xl font-bold text-green-600">₹{feeStructure.online_monthly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per month</p>
                    </Card>
                    <Card className="p-4 bg-white border-green-200">
                      <p className="text-xs text-gray-600 mb-1">Quarterly</p>
                      <p className="text-3xl font-bold text-green-600">₹{feeStructure.online_quarterly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per 3 months</p>
                    </Card>
                    <Card className="p-4 bg-white border-green-200">
                      <p className="text-xs text-gray-600 mb-1">Yearly</p>
                      <p className="text-3xl font-bold text-green-600">₹{feeStructure.online_yearly || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">per year</p>
                    </Card>
                  </div>
                </Card>

                {/* Additional Fees */}
                <Card className="p-6">
                  <h4 className="text-lg font-bold mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
                    Additional Charges
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                      <p className="text-sm text-gray-700 mb-2">Registration Fee</p>
                      <p className="text-2xl font-bold text-purple-600">₹{feeStructure.registration_fee || 0}</p>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                      <p className="text-sm text-gray-700 mb-2">Kit Fee</p>
                      <p className="text-2xl font-bold text-orange-600">₹{feeStructure.kit_fee || 0}</p>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
                      <p className="text-sm text-gray-700 mb-2">Royalty Percentage</p>
                      <p className="text-2xl font-bold text-cyan-600">{feeStructure.royalty_percentage || 0}%</p>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200">
                      <p className="text-sm text-gray-700 mb-2">Effective From</p>
                      <p className="text-xl font-bold text-gray-700">
                        {feeStructure.effective_from 
                          ? new Date(feeStructure.effective_from).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })
                          : 'N/A'}
                      </p>
                    </Card>
                  </div>
                </Card>
              </div>
            ) : (
              <Card className="p-12">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <DollarSign className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-700">No Fee Structure Available</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Fee structure has not been set up for your center yet. Click the button below to request one from the admin.
                  </p>
                  <Button
                    onClick={() => setRequestDialog(true)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 mt-4"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Request Fee Structure
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Request Fee Structure Dialog */}
      <Dialog open={requestDialog} onOpenChange={setRequestDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              <Send className="w-5 h-5 mr-2 text-blue-600" />
              Propose Fee Structure Update
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="request-message" className="text-sm font-semibold">
                Message to Admin *
              </Label>
              <textarea
                id="request-message"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Explain the reason for fee structure update or new proposal..."
                className="w-full mt-2 p-3 border rounded-lg min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {requestMessage.length}/500 characters
              </p>
            </div>

            {/* Proposed Fees */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Proposed Fees (Fill at least one)</h4>
              
              {/* Offline Fees */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-semibold text-blue-900 mb-3">Offline Fees</h5>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Monthly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.offline_monthly}
                      onChange={(e) => setProposedFees({...proposedFees, offline_monthly: e.target.value})}
                      placeholder="e.g., 2000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quarterly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.offline_quarterly}
                      onChange={(e) => setProposedFees({...proposedFees, offline_quarterly: e.target.value})}
                      placeholder="e.g., 5500"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Yearly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.offline_yearly}
                      onChange={(e) => setProposedFees({...proposedFees, offline_yearly: e.target.value})}
                      placeholder="e.g., 20000"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Online Fees */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-semibold text-green-900 mb-3">Online Fees</h5>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Monthly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.online_monthly}
                      onChange={(e) => setProposedFees({...proposedFees, online_monthly: e.target.value})}
                      placeholder="e.g., 1500"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quarterly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.online_quarterly}
                      onChange={(e) => setProposedFees({...proposedFees, online_quarterly: e.target.value})}
                      placeholder="e.g., 4000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Yearly (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.online_yearly}
                      onChange={(e) => setProposedFees({...proposedFees, online_yearly: e.target.value})}
                      placeholder="e.g., 15000"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Fees */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-semibold text-purple-900 mb-3">Additional Fees</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Registration Fee (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.registration_fee}
                      onChange={(e) => setProposedFees({...proposedFees, registration_fee: e.target.value})}
                      placeholder="e.g., 500"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Kit Fee (₹)</Label>
                    <Input
                      type="number"
                      value={proposedFees.kit_fee}
                      onChange={(e) => setProposedFees({...proposedFees, kit_fee: e.target.value})}
                      placeholder="e.g., 1000"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-sm text-amber-900">
                <strong>Note:</strong> Your proposed fees will be sent to the admin for review and approval. 
                You'll be notified once the admin takes action.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setRequestDialog(false);
                  setRequestMessage('');
                  setProposedFees({
                    offline_monthly: '',
                    offline_quarterly: '',
                    offline_yearly: '',
                    online_monthly: '',
                    online_quarterly: '',
                    online_yearly: '',
                    registration_fee: '',
                    kit_fee: ''
                  });
                }}
                className="flex-1"
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestFeeStructure}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={sending || !requestMessage.trim()}
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Proposal
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherProfile;
